import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle, XCircle, Spinner, ArrowRight } from '@phosphor-icons/react';

import { API_URL } from '../config';

const SubscriptionSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { refreshUser, isAnnual } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/api/subscription/status/${sessionId}`,
          { withCredentials: true }
        );

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          await refreshUser();
          return;
        }

        if (response.data.status === 'expired') {
          setStatus('failed');
          return;
        }

        // Continue polling
        if (attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('failed');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('failed');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, attempts, refreshUser]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 flex items-center justify-center">
      <div className="max-w-md w-full mx-auto px-4 text-center">
        {status === 'checking' && (
          <div className="bg-[#131313] border border-white/10 p-12" data-testid="payment-checking">
            <Spinner size={64} className="mx-auto text-[#007AFF] animate-spin mb-6" />
            <h1 className="font-heading font-bold text-2xl uppercase text-white mb-3">
              Processing Payment
            </h1>
            <p className="text-[#8892B0]">
              Please wait while we confirm your payment...
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-[#131313] border border-white/10 p-12" data-testid="payment-success">
            <CheckCircle size={64} weight="fill" className="mx-auto text-[#34C759] mb-6" />
            <h1 className="font-heading font-bold text-2xl uppercase text-white mb-3">
              Welcome to Premium!
            </h1>
            <p className="text-[#8892B0] mb-8">
              Your subscription is now active. Thank you for your support!
            </p>
            
            {isAnnual && (
              <div className="mb-8 p-4 bg-[#34C759]/10 border border-[#34C759]/30">
                <p className="text-[#34C759] font-bold mb-2">Exclusive Benefit Unlocked!</p>
                <p className="text-[#8892B0] text-sm">
                  As an annual subscriber, you now have access to 1-on-1 human coaching via Telegram.
                </p>
                <a
                  href={process.env.REACT_APP_TELEGRAM_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-3 text-[#34C759] hover:underline"
                >
                  Contact Your Coach <ArrowRight size={16} />
                </a>
              </div>
            )}

            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#007AFF] text-white font-bold uppercase tracking-widest hover:bg-[#005bb5] transition-colors"
              data-testid="go-to-dashboard"
            >
              Go to Dashboard <ArrowRight size={20} />
            </Link>
          </div>
        )}

        {status === 'failed' && (
          <div className="bg-[#131313] border border-white/10 p-12" data-testid="payment-failed">
            <XCircle size={64} weight="fill" className="mx-auto text-[#FF3B30] mb-6" />
            <h1 className="font-heading font-bold text-2xl uppercase text-white mb-3">
              Payment Issue
            </h1>
            <p className="text-[#8892B0] mb-8">
              We couldn't confirm your payment. Please try again or contact support.
            </p>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#007AFF] text-white font-bold uppercase tracking-widest hover:bg-[#005bb5] transition-colors"
              data-testid="back-to-pricing"
            >
              Back to Pricing
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;
